import React from 'react';
import { Card, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import type { AlertMessageKey, AlertSeverity } from '../models/alert';
import { getSeverityColor, getSeverityIcon } from '../utils/alertSeverity';

const useStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxHeight: '380px',
    overflowY: 'auto',
    paddingLeft: '2px',
    paddingRight: '4px',
    paddingBottom: '2px',
  },
  card: {
    cursor: 'pointer',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minHeight: '100px',
    flexShrink: 0,
    overflow: 'visible',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  cardSelected: {
    backgroundColor: tokens.colorNeutralBackground3,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  cardReadOnly: {
    cursor: 'default',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1,
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  icon: {
    fontSize: '18px',
    flexShrink: 0,
  },
  title: {
    display: 'block',
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  body: {
    display: 'block',
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
});

export interface MessageCardOption {
  key: AlertMessageKey;
  title: string;
  body: string;
  severity: AlertSeverity;
}

interface MessageCardListProps {
  options: readonly MessageCardOption[];
  selectedKey: AlertMessageKey | null;
  onSelect: (key: AlertMessageKey) => void;
  /**
   * When true, only the selected card is rendered and the card is
   * non-interactive (no click/keyboard select, no hover state change).
   */
  readOnly?: boolean;
  ariaLabelledBy?: string;
}

/**
 * Radiogroup of alert message templates rendered as selectable cards, each
 * showing the severity icon, title, and a filled-in sample body.
 */
export const MessageCardList: React.FC<MessageCardListProps> = ({
  options,
  selectedKey,
  onSelect,
  readOnly = false,
  ariaLabelledBy,
}) => {
  const styles = useStyles();
  const visible = readOnly ? options.filter((o) => o.key === selectedKey) : options;

  return (
    <div className={styles.list} role="radiogroup" aria-labelledby={ariaLabelledBy}>
      {visible.map(({ key, title, body, severity }) => {
        const isSelected = selectedKey === key;
        const Icon = getSeverityIcon(severity);
        return (
          <Card
            key={key}
            role="radio"
            aria-checked={isSelected}
            aria-readonly={readOnly || undefined}
            tabIndex={readOnly ? -1 : 0}
            onClick={readOnly ? undefined : () => onSelect(key)}
            onKeyDown={
              readOnly
                ? undefined
                : (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(key);
                    }
                  }
            }
            className={mergeClasses(
              styles.card,
              isSelected && !readOnly && styles.cardSelected,
              readOnly && styles.cardReadOnly,
            )}
          >
            <div className={styles.header}>
              <Icon
                className={styles.icon}
                style={{ color: getSeverityColor(severity) }}
              />
              <div className={styles.title}>{title}</div>
            </div>
            <div className={styles.body}>{body}</div>
          </Card>
        );
      })}
    </div>
  );
};
